/**
 * The personas an account offers, as the exact value strings the Switch Account dropdown uses.
 *
 * ONE SOURCE OF TRUTH, which is the whole reason this is a module rather than two loops inside the
 * dialog. The default and the menu items were built separately and disagreed four ways: the default
 * read `companyUSID` while the items read `CompanyUSID` and the payload says `AccountUSID`; the
 * default read `account["Positions"]["USID"]` -- the ARRAY, not its first element; the two spelled
 * a user group differently from each other; and `ChangeRealm` had the account key wrong too, so
 * the switch never told the server which realm to change to and the session stayed where it was.
 *
 * The default therefore came out `"undefined:Position:undefined"` -- TRUTHY, so it passed every
 * guard -- matched no option, left the Persona field BLANK, and was submitted as the visibility.
 *
 * Found on screen on 2026-10-17 switching account as a person who holds two, after the live symptom
 * was CC-PRSN-DENY at 403 on every read.
 */

export type PersonaOption = {value: string; label: string; group: string};

/** The mode each group carries, spelled the way the SERVER reads it.
 *
 *  SINGULAR, AND THAT IS LOAD-BEARING. `cres/utils/visibility._canonical_mode` squashes a mode to
 *  its alphanumerics and looks it up in a table derived from `_MEMBERSHIP`'s own keys -- so
 *  `UserGroup`, `User Group` and `usergroup` all reach `"User Group"`, and `UserGroups` reaches
 *  NOTHING. An unknown mode denies rather than widens, so the plural does not fail loudly: it
 *  shows an empty application, indistinguishable from "nothing is shared with you". An earlier
 *  version of this file carried the plural with a comment insisting it was deliberate.
 */
const GROUPS: [string, string][] = [
    ["Positions", "Position"],
    ["UserGroups", "UserGroup"],
    ["WorkGroups", "WorkGroup"],
];

export function personaOptionsFor(companies: any, accountName: string): PersonaOption[] {
    const account = (companies || {})[accountName];
    if (!account) return [];

    // AccountUSID, read from the live authenticate response rather than from the source line next
    // door -- which is the line that was wrong.
    const accountUSID = account["AccountUSID"];

    return GROUPS.flatMap(([key, mode]) => (account[key] || []).map((one: any) => ({
        value: `${accountUSID}:${mode}:${one["USID"]}`,
        label: one["Name"],
        group: key,
    })));
}

/** The first option the dropdown will show, so the default is BY CONSTRUCTION a value the list
 *  contains. Null when the account offers nothing -- null, not a string with `undefined` in it,
 *  because null is what the caller checks for. */
export function defaultVisibilityFor(companies: any, accountName: string): string | null {
    const options = personaOptionsFor(companies, accountName);
    return options.length > 0 ? options[0].value : null;
}
